'use client';

import type { Column } from '@tanstack/react-table';
import { EyeOff, GripVertical, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from '@/components/ui/chevron-down';
import { ChevronUpIcon } from '@/components/ui/chevron-up';
import { ChevronsUpDownIcon } from '@/components/ui/chevrons-up-down';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SortableItemHandle } from '@/components/ui/sortable';
import { cn } from '@/lib/utils';

function ColumnDragHandle({ canReorder }: { canReorder: boolean }) {
  if (!canReorder) {
    return null;
  }

  return (
    <SortableItemHandle
      className='cursor-grab touch-none select-none text-muted-foreground hover:text-foreground [&_svg]:size-4'
      onClick={(e) => e.stopPropagation()}
    >
      <GripVertical />
    </SortableItemHandle>
  );
}

interface DataTableColumnHeaderProps<TData, TValue>
  extends React.ComponentProps<typeof DropdownMenuTrigger> {
  column: Column<TData, TValue>;
  label: string;
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  label,
  className,
  ...props
}: DataTableColumnHeaderProps<TData, TValue>) {
  const { t } = useTranslation(['table']);
  const [isHovered, setIsHovered] = useState(false);
  const canReorder = column.columnDef.meta?.enableColumnReordering !== false;

  if (!(column.getCanSort() || column.getCanHide())) {
    return (
      <div className={cn('flex items-center gap-1', className)}>
        <ColumnDragHandle canReorder={canReorder} />
        <span>{label}</span>
      </div>
    );
  }

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <ColumnDragHandle canReorder={canReorder} />
      <DropdownMenu>
        <DropdownMenuTrigger
          className={cn(
            '-ml-1.5 flex h-8 items-center gap-1.5 rounded-md px-2 py-1.5 hover:bg-accent focus:outline-none focus:ring-1 focus:ring-ring data-[state=open]:bg-accent [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-muted-foreground',
          )}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          {...props}
        >
          {label}
          {column.getCanSort() &&
            (column.getIsSorted() === 'desc' ? (
              <ChevronDownIcon isAnimating={isHovered} size={16} />
            ) : column.getIsSorted() === 'asc' ? (
              <ChevronUpIcon isAnimating={isHovered} size={16} />
            ) : (
              <ChevronsUpDownIcon isAnimating={isHovered} size={16} />
            ))}
        </DropdownMenuTrigger>
        <DropdownMenuContent align='start' className='w-28'>
          {column.getCanSort() && (
            <>
              <DropdownMenuCheckboxItem
                checked={column.getIsSorted() === 'asc'}
                className='relative pr-8 pl-2 [&>span:first-child]:right-2 [&>span:first-child]:left-auto [&_svg]:text-muted-foreground'
                onClick={() => column.toggleSorting(false)}
              >
                <ChevronUpIcon size={16} />
                Asc
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={column.getIsSorted() === 'desc'}
                className='relative pr-8 pl-2 [&>span:first-child]:right-2 [&>span:first-child]:left-auto [&_svg]:text-muted-foreground'
                onClick={() => column.toggleSorting(true)}
              >
                <ChevronDownIcon size={16} />
                Desc
              </DropdownMenuCheckboxItem>
              {column.getIsSorted() && (
                <DropdownMenuItem
                  className='pl-2 [&_svg]:text-muted-foreground'
                  onClick={() => column.clearSorting()}
                >
                  <X />
                  {t('Reset')}
                </DropdownMenuItem>
              )}
            </>
          )}
          {column.getCanHide() && (
            <DropdownMenuCheckboxItem
              checked={!column.getIsVisible()}
              className='relative pr-8 pl-2 [&>span:first-child]:right-2 [&>span:first-child]:left-auto [&_svg]:text-muted-foreground'
              onClick={() => column.toggleVisibility(false)}
            >
              <EyeOff />
              {t('Hide')}
            </DropdownMenuCheckboxItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
